import { Injectable, EnvironmentInjector, ApplicationRef, Injector } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MetaService } from './meta.service';
import { LLMActionsInjectionController } from './llm-actions-injection-controller';

interface MetadataConfig {
  template?: string;
  'enable-llms-actions'?: boolean;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class LLMActionsInjectorService {
  private readonly llmActionTemplates = ['doc-page', 'prod-doc-page'];

  constructor(
    private environmentInjector: EnvironmentInjector,
    private appRef: ApplicationRef,
    private injector: Injector,
    private router: Router,
    private metaService: MetaService
  ) {}

  setupAutoInjection(selector: string, destroy$: Subject<void>, componentRoute: ActivatedRoute): void {
    let injectionController: LLMActionsInjectionController | null = null;

    this.metaService.metadata.pipe(takeUntil(destroy$)).subscribe((metadata: MetadataConfig) => {
      if (this.shouldShowLlmActions(metadata)) {
        if (!injectionController) {
          injectionController = this.injectIntoSelector(selector, componentRoute);
          injectionController.inject();
        }
      } else if (injectionController) {
        injectionController.remove();
        injectionController = null;
      }
    });

    destroy$.subscribe(() => {
      if (injectionController) {
        injectionController.remove();
        injectionController = null;
      }
    });
  }

  private injectIntoSelector(selector: string, componentRoute: ActivatedRoute): LLMActionsInjectionController {
    return new LLMActionsInjectionController(selector, {
      environmentInjector: this.environmentInjector,
      appRef: this.appRef,
      injector: this.injector,
      router: this.router,
      route: componentRoute,
    });
  }

  private shouldShowLlmActions(metadata: MetadataConfig): boolean {
    const enableLlmActions = metadata?.['enable-llms-actions'];

    if (typeof enableLlmActions !== 'boolean') {
      return false;
    }

    if (!enableLlmActions) {
      return false;
    }

    return this.isValidTemplate(metadata?.template);
  }

  private isValidTemplate(template: unknown): boolean {
    if (typeof template !== 'string') {
      return false;
    }
    return this.llmActionTemplates.includes(template);
  }
}
